import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
    FaEnvelope, FaPhone, FaMapMarkerAlt,
    FaPaperPlane, FaCheckCircle
} from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const BASE_URL = "https://api.dreamtoday.in";

const ContactUs = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [companyContact, setCompanyContact] = useState(null);

    useEffect(() => {
        fetch(`${BASE_URL}/api/admin/allcompanycontacts`)
            .then((r) => r.json())
            .then((d) => {
                if (d.success && d.contacts?.length > 0) setCompanyContact(d.contacts[0]);
            })
            .catch(() => {});
    }, []);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
        setSubmitError("");
    };

    const validate = () => {
        const errs = {};
        if (!form.name.trim()) errs.name = "Full name is required.";
        if (!form.email.trim()) {
            errs.email = "Email is required.";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            errs.email = "Enter a valid email address.";
        }
        if (form.phone.trim() && !/^\d{10}$/.test(form.phone.replace(/\s/g, ""))) {
            errs.phone = "Enter a valid 10-digit phone number.";
        }
        if (!form.message.trim()) {
            errs.message = "Message is required.";
        } else if (form.message.trim().length < 10) {
            errs.message = "Message must be at least 10 characters.";
        }
        return errs;
    };

    const handleSubmit = async () => {
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setLoading(true);
        setSubmitError("");
        try {
            const res = await fetch(`${BASE_URL}/api/admin/createcontactus`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: form.name.trim(),
                    phone: form.phone.trim(),
                    email: form.email.trim(),
                    message: form.message.trim(),
                }),
            });
            const data = await res.json();
            if (data.success) {
                setSubmitted(true);
            } else {
                setSubmitError(data.message || "Failed to send message. Please try again.");
            }
        } catch {
            setSubmitError("Network error. Please check your connection and try again.");
        } finally {
            setLoading(false);
        }
    };

    const inputClass = (field) =>
        `w-full px-5 py-3.5 rounded-2xl border bg-white/70 backdrop-blur-sm outline-none text-gray-800
        focus:ring-2 focus:ring-yellow-100 transition placeholder:text-gray-400
        ${errors[field] ? "border-red-300 focus:border-red-400" : "border-yellow-200 focus:border-yellow-400"}`;

    const contacts = [
        {
            icon: FaEnvelope,
            label: "Email Us",
            value: companyContact?.email || "support@prizepulse.com",
            sub: "We reply within 24 hours",
        },
        {
            icon: FaPhone,
            label: "Call Us",
            value: companyContact?.mobile ? `+91 ${companyContact.mobile}` : "+91 98765 43210",
            sub: "Mon – Sat, 9AM – 6PM",
        },
        {
            icon: FaMapMarkerAlt,
            label: "Our Office",
            value: companyContact?.address || "Hyderabad, Telangana",
            sub: "India – 500081",
        },
    ];

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-gradient-to-b from-white via-[#FFF7DB] to-white pt-28 pb-20 px-4 relative overflow-hidden">

                {/* BACKGROUND DECORATION */}
                <div className="absolute top-0 left-0 w-96 h-96 rounded-full
                    bg-gradient-to-br from-yellow-100 to-amber-100 opacity-40 blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
                <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full
                    bg-gradient-to-br from-amber-100 to-yellow-200 opacity-40 blur-3xl translate-x-1/3 translate-y-1/3 pointer-events-none" />

                <div className="max-w-6xl mx-auto relative z-10">

                    {/* HERO */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center mb-16"
                    >
                        <h1 className="text-4xl md:text-5xl font-bold mb-4
                            bg-gradient-to-r from-yellow-600 via-yellow-700 to-yellow-800
                            bg-clip-text text-transparent">
                            Get In Touch
                        </h1>
                        <p className="text-gray-500 max-w-xl mx-auto text-base">
                            Have a question or need help? We're always happy to hear from you.
                        </p>
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-12 items-start">

                        {/* LEFT — CONTACT CARDS */}
                        <motion.div
                            initial={{ opacity: 0, x: -40 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 }}
                            className="space-y-6"
                        >
                            {contacts.map((c, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.15 + i * 0.1 }}
                                    className="flex items-start gap-5 bg-white/80 backdrop-blur-xl
                                        border border-yellow-200 rounded-3xl p-6 shadow-md hover:shadow-lg
                                        hover:-translate-y-1 transition duration-300"
                                >
                                    <div className="w-14 h-14 shrink-0 rounded-2xl
                                        bg-gradient-to-br from-yellow-400 to-amber-500
                                        flex items-center justify-center shadow-md">
                                        <c.icon className="text-white text-xl" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-400 font-medium mb-0.5">{c.label}</p>
                                        <p className="font-bold text-gray-800 text-base">{c.value}</p>
                                        <p className="text-sm text-gray-400 mt-0.5">{c.sub}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>

                        {/* RIGHT — FORM */}
                        <motion.div
                            initial={{ opacity: 0, x: 40 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 }}
                            className="bg-white/80 backdrop-blur-xl border border-yellow-200 rounded-3xl p-8 shadow-xl"
                        >
                            {submitted ? (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="flex flex-col items-center justify-center py-12 text-center"
                                >
                                    <div className="w-20 h-20 rounded-full bg-green-50 border-2 border-green-200
                                        flex items-center justify-center mb-5 shadow">
                                        <FaCheckCircle className="text-green-500 text-4xl" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-800 mb-2">Message Sent!</h3>
                                    <p className="text-gray-500 text-sm max-w-xs">
                                        Thanks for reaching out. We'll get back to you within 24 hours.
                                    </p>
                                    <button
                                        onClick={() => {
                                            setSubmitted(false);
                                            setForm({ name: "", email: "", phone: "", message: "" });
                                            setErrors({});
                                        }}
                                        className="mt-6 px-6 py-2.5 rounded-full text-sm font-semibold text-yellow-700
                                            border border-yellow-300 hover:bg-yellow-50 transition"
                                    >
                                        Send Another
                                    </button>
                                </motion.div>
                            ) : (
                                <>
                                    <h2 className="text-2xl font-bold text-gray-800 mb-6">Send a Message</h2>

                                    <div className="space-y-4">
                                        <div className="grid sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-xs text-gray-500 font-medium mb-1 block">Full Name *</label>
                                                <input
                                                    name="name"
                                                    placeholder="Your name"
                                                    className={inputClass("name")}
                                                    value={form.name}
                                                    onChange={handleChange}
                                                />
                                                {errors.name && (
                                                    <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                                                )}
                                            </div>
                                            <div>
                                                <label className="text-xs text-gray-500 font-medium mb-1 block">Phone</label>
                                                <input
                                                    name="phone"
                                                    placeholder="10-digit number"
                                                    className={inputClass("phone")}
                                                    value={form.phone}
                                                    onChange={handleChange}
                                                    maxLength={10}
                                                />
                                                {errors.phone && (
                                                    <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-xs text-gray-500 font-medium mb-1 block">Email *</label>
                                            <input
                                                name="email"
                                                type="email"
                                                placeholder="you@example.com"
                                                className={inputClass("email")}
                                                value={form.email}
                                                onChange={handleChange}
                                            />
                                            {errors.email && (
                                                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-xs text-gray-500 font-medium mb-1 block">Message *</label>
                                            <textarea
                                                name="message"
                                                rows={5}
                                                placeholder="How can we help you? (min. 10 characters)"
                                                className={`${inputClass("message")} resize-none`}
                                                value={form.message}
                                                onChange={handleChange}
                                            />
                                            <div className="flex justify-between items-center mt-1">
                                                {errors.message
                                                    ? <p className="text-red-500 text-xs">{errors.message}</p>
                                                    : <span />
                                                }
                                                <p className={`text-xs ml-auto ${form.message.length < 10 ? "text-gray-400" : "text-green-500"}`}>
                                                    {form.message.length} chars
                                                </p>
                                            </div>
                                        </div>

                                        {/* API ERROR */}
                                        {submitError && (
                                            <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                                                ⚠️ {submitError}
                                            </div>
                                        )}

                                        <button
                                            onClick={handleSubmit}
                                            disabled={loading}
                                            className="w-full py-4 rounded-2xl text-white font-semibold text-base
                                                bg-gradient-to-r from-yellow-500 to-amber-600
                                                hover:scale-[1.02] transition shadow-lg
                                                disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            {loading ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                    Sending...
                                                </>
                                            ) : (
                                                <>
                                                    <FaPaperPlane /> Send Message
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </>
                            )}
                        </motion.div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default ContactUs;